import { useCallback, useEffect, useRef } from "react";

import { useSettingsStore } from "@/store/settingsStore";

const isSpeechSupported = typeof window !== "undefined" && window.speechSynthesis != null;

/** 네이티브 앱 WebView에서 주입된 SpeechBridge 사용 가능 여부 */
const isBridgeAvailable = () =>
  typeof window !== "undefined" && window.SpeechBridge != null && typeof window.SpeechBridge.speak === "function";

export function useSpeech() {
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const isMuted = useSettingsStore(state => state.isMuted);
  const toggleMute = useSettingsStore(state => state.toggleMute);

  useEffect(() => {
    if (!isSpeechSupported) {
      return;
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const preferred = [
        // 영국
        "Google UK English Female",
        "Serena",
        // 호주
        "Google AU English Male",
        "Google Australian English Male",
        "Daniel",
        // 미국
        "Google US English",
        "Aaron",
        "Samantha",
        "Reed",
        // 한국어
        "Google KR Korean Male",
        "Google KR Korean Male",
      ];
      voicesRef.current = voices.filter(v => v.lang === "en-US" && preferred.includes(v.name));
    };

    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);

    return () => window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, []);

  const speak = useCallback((text: string) => {
    // 1순위: Web Speech API (iOS WebView, 데스크톱 브라우저)
    if (isSpeechSupported) {
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      if (voicesRef.current.length > 0) {
        const randomIndex = Math.floor(Math.random() * voicesRef.current.length);
        utterance.voice = voicesRef.current[randomIndex];
      }

      utterance.lang = "en-US";
      window.speechSynthesis.speak(utterance);
      return;
    }

    // 2순위: 네이티브 브릿지 (Android WebView)
    if (isBridgeAvailable()) {
      window.SpeechBridge.speak(text, "en-US");
      return;
    }

    // 둘 다 없으면 무시
  }, []);

  return { speak, isMuted, toggleMute };
}
